import { useState, useEffect, useRef } from 'react';
import { sortByName } from '../lib/sortByName';
import { useTranslation } from 'react-i18next';
import { reportsAPI, organizationsAPI, reportSchedulesAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import ScheduleReportModal from '../components/ScheduleReportModal';
import ScheduledReportsList from '../components/ScheduledReportsList';
import PdfBrandingSettings from '../components/PdfBrandingSettings';
import PdfPreviewModal from '../components/PdfPreviewModal';
import {
    FileText,
    Download,
    Eye,
    Calendar,
    Building2,
    Users,
    MapPin,
    CreditCard,
    TrendingUp,
    TrendingDown,
    BarChart3,
    PieChart,
    Lightbulb,
    Loader2,
    CheckCircle,
    AlertCircle,
    CalendarClock,
    ShieldCheck,
    Gift,
    Globe
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    ResponsiveContainer,
    PieChart as RechartsPie,
    Pie,
    Cell,
    LineChart,
    Line
} from 'recharts';

const PLATFORM_COLORS = {
    google_reviews: '#FF00FF',
    facebook: '#1877F2',
    instagram: '#E4405F',
    trustpilot: '#00B67A',
    yelp: '#D32323',
    tripadvisor: '#34E0A1'
};

const PLATFORM_LABELS = {
    google_reviews: 'Google Reviews',
    facebook: 'Facebook',
    instagram: 'Instagram',
    trustpilot: 'Trustpilot',
    yelp: 'Yelp',
    tripadvisor: 'Tripadvisor'
};

const SalesReport = () => {
    const { t, i18n } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState(null);
    const [organizations, setOrganizations] = useState([]);
    // SEVERAL ORGANIZATIONS: "All organizations" is the master tick (default),
    // otherwise an explicit list. One organization alone keeps every
    // single-organization behaviour, branding included.
    const [allOrgs, setAllOrgs] = useState(true);
    const [selectedOrgIds, setSelectedOrgIds] = useState([]);
    const [orgSearch, setOrgSearch] = useState('');
    const [showPreview, setShowPreview] = useState(false);
    const [generatingPdf, setGeneratingPdf] = useState(false);
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [editingSchedule, setEditingSchedule] = useState(null);
    const [scheduleRefresh, setScheduleRefresh] = useState(0);
    const [pdfPreview, setPdfPreview] = useState({ open: false, loading: false, error: false, pages: [] });
    
    // Date range
    const now = new Date();
    const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const [startDate, setStartDate] = useState(firstOfMonth.toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState(now.toISOString().split('T')[0]);
    const [reportType, setReportType] = useState('custom');
    const [reportLanguage, setReportLanguage] = useState('preferred');
    
    // Report sections
    const [sections, setSections] = useState({
        roi: true,
        platforms: true,
        employees: true,
        locations: true,
        timeline: true,
        topPerformers: true
    });
    
    const { user, isSuperAdmin } = useAuth();
    const reportRef = useRef(null);

    const selectedOrg = (!allOrgs && selectedOrgIds.length === 1) ? selectedOrgIds[0] : '';
    const orgIdsForRequest = allOrgs ? [] : selectedOrgIds;
    const noneChosen = !allOrgs && selectedOrgIds.length === 0;
    const orgTicked = (id) => allOrgs || selectedOrgIds.includes(id);
    const toggleOrg = (id) => {
        if (allOrgs) {
            setAllOrgs(false);
            setSelectedOrgIds(organizations.map(o => o.id).filter(o => o !== id));
            return;
        }
        setSelectedOrgIds(ids => ids.includes(id) ? ids.filter(o => o !== id) : [...ids, id]);
    };
    const onlyThisOrg = (id) => { setAllOrgs(false); setSelectedOrgIds([id]); };
    const toggleAllOrgs = () => {
        if (allOrgs) { setAllOrgs(false); setSelectedOrgIds([]); }
        else { setAllOrgs(true); setSelectedOrgIds([]); }
    };
    const visibleOrgs = organizations.filter(o =>
        (o.name || '').toLowerCase().includes(orgSearch.trim().toLowerCase()));

    useEffect(() => {
        loadOrganizations();
    }, []);

    useEffect(() => {
        updateDateRange(reportType);
    }, [reportType]);

    const loadOrganizations = async () => {
        try {
            const res = await organizationsAPI.list();
            setOrganizations(sortByName(res.data));
        } catch (error) {
            console.error('Failed to load organizations');
        }
    };

    const updateDateRange = (type) => {
        const today = new Date();
        let start, end;
        
        switch (type) {
            case 'monthly':
                start = new Date(today.getFullYear(), today.getMonth(), 1);
                end = today;
                break;
            case 'quarterly':
                const quarter = Math.floor(today.getMonth() / 3);
                start = new Date(today.getFullYear(), quarter * 3, 1);
                end = today;
                break;
            case 'annual':
                start = new Date(today.getFullYear(), 0, 1);
                end = today;
                break;
            default:
                return; // Custom - don't change
        }
        
        setStartDate(start.toISOString().split('T')[0]);
        setEndDate(end.toISOString().split('T')[0]);
    };

    const pdfLanguage = () => {
        const uiLang = (i18n.language || 'en').split('-')[0];
        const fallback = ['en', 'fr', 'es'].includes(uiLang) ? uiLang : 'en';
        return reportLanguage !== 'preferred' ? reportLanguage : fallback;
    };

    const generateReport = async () => {
        setLoading(true);
        setShowPreview(false);
        
        try {
            const res = await reportsAPI.getSalesReport(startDate, endDate, selectedOrg || undefined, orgIdsForRequest);
            setReportData(res.data);
            setShowPreview(true);
            toast.success(t('salesReport.toasts.generated'));
        } catch (error) {
            toast.error(error.response?.data?.detail || t('salesReport.previewFailed'));
        } finally {
            setLoading(false);
        }
    };

    // True PDF preview — generates the exact server-rendered PDF Download
    // produces and shows its pages in-app (server-rasterized, so it works
    // on every browser including iPad Safari).
    const previewPdf = async () => {
        setPdfPreview({ open: true, loading: true, error: false, pages: [] });
        try {
            const res = await reportSchedulesAPI.salesPdfImages({
                organization_id: selectedOrg || null,
                organization_ids: orgIdsForRequest.length ? orgIdsForRequest : null,
                start_date: startDate,
                end_date: endDate,
                language: pdfLanguage(),
                sections,
            });
            const pages = res.data?.pages || [];
            setPdfPreview({ open: true, loading: false, error: !pages.length, pages });
        } catch (error) {
            setPdfPreview({ open: true, loading: false, error: true, pages: [] });
        }
    };

    const quickReport = async (type) => {
        const today = new Date();
        let start, end;
        
        switch (type) {
            case 'this_month':
                start = new Date(today.getFullYear(), today.getMonth(), 1);
                end = today;
                break;
            case 'last_month':
                start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                end = new Date(today.getFullYear(), today.getMonth(), 0);
                break;
            case 'qtd':
                const quarter = Math.floor(today.getMonth() / 3);
                start = new Date(today.getFullYear(), quarter * 3, 1);
                end = today;
                break;
            case 'ytd':
                start = new Date(today.getFullYear(), 0, 1);
                end = today;
                break;
            default:
                return;
        }
        
        setStartDate(start.toISOString().split('T')[0]);
        setEndDate(end.toISOString().split('T')[0]);
        
        setLoading(true);
        try {
            const res = await reportsAPI.getSalesReport(
                start.toISOString().split('T')[0],
                end.toISOString().split('T')[0],
                selectedOrg || undefined,
                orgIdsForRequest
            );
            setReportData(res.data);
            setShowPreview(true);
            toast.success(t('salesReport.toasts.generated'));
        } catch (error) {
            toast.error(t('salesReport.toasts.generateFailed'));
        } finally {
            setLoading(false);
        }
    };

    const downloadPdf = async () => {
        if (!reportData) {
            toast.error(t('salesReport.toasts.generateFirst'));
            return;
        }

        setGeneratingPdf(true);
        toast.info(t('salesReport.toasts.pdfDownloading'));

        try {
            // Unified engine: the server renders the exact same branded PDF
            // used by scheduled deliveries and previews (saved branding
            // preferences applied automatically).
            const res = await reportSchedulesAPI.downloadSalesPdf({
                organization_id: selectedOrg || null,
                organization_ids: orgIdsForRequest.length ? orgIdsForRequest : null,
                start_date: startDate,
                end_date: endDate,
                language: pdfLanguage(),
                sections,
            });
            const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = `YulOnTap_Report_${startDate}_to_${endDate}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success(t('salesReport.toasts.pdfDownloaded'));
        } catch (error) {
            console.error('[PDF] generation failed', {
                message: error?.message,
                status: error?.response?.status,
            });
            toast.error(t('salesReport.pdfPreview.failed'));
        } finally {
            setGeneratingPdf(false);
        }
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2
        }).format(value || 0);
    };

    // Prepare chart data
    const platformChartData = Object.entries(reportData?.platform_breakdown || {}).map(([platform, data]) => ({
        name: PLATFORM_LABELS[platform] || platform,
        value: data.total_value,
        count: data.count,
        fill: PLATFORM_COLORS[platform] || '#888'
    }));

    return (
        <div className="space-y-6" data-testid="sales-report">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {t('salesReport.pageTitle')}
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        {t('salesReport.pageSubtitle')}
                    </p>
                </div>
            </div>

            {/* Report Configuration */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <FileText className="w-5 h-5 text-purple-600" />
                        {t('salesReport.generateTitle')}
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                    {/* Organization Filter (Super Admin) */}
                    {isSuperAdmin() && organizations.length > 0 && (
                        <div data-testid="report-org-picker">
                            <Label className="mb-2 block">{t('common.organization')}</Label>
                            <div className="w-full max-w-md rounded-lg border border-border">
                                <div className="border-b border-border p-2">
                                    <input
                                        value={orgSearch}
                                        onChange={(e) => setOrgSearch(e.target.value)}
                                        placeholder={t('salesReport.orgPicker.search', 'Search organizations')}
                                        data-testid="report-org-search"
                                        className="w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-sm"
                                    />
                                </div>
                                <label className="flex cursor-pointer items-center gap-2 border-b border-border px-3 py-2 font-medium">
                                    <Checkbox checked={allOrgs} onCheckedChange={toggleAllOrgs}
                                              data-testid="report-org-all" />
                                    <Building2 className="h-4 w-4 text-muted-foreground" />
                                    <span className="text-sm">{t('roiDashboard.allOrganizations')}</span>
                                </label>
                                <div className="max-h-56 overflow-y-auto" data-testid="report-org-list">
                                    {visibleOrgs.map(org => (
                                        <div key={org.id}
                                             className="flex items-center gap-2 px-3 py-1.5 hover:bg-muted/50">
                                            <Checkbox checked={orgTicked(org.id)}
                                                      onCheckedChange={() => toggleOrg(org.id)}
                                                      data-testid={`report-org-${org.id}`} />
                                            <span className="min-w-0 flex-1 truncate text-sm">{org.name}</span>
                                            <button type="button" onClick={() => onlyThisOrg(org.id)}
                                                    data-testid={`report-org-only-${org.id}`}
                                                    className="shrink-0 text-xs text-muted-foreground underline hover:text-foreground">
                                                {t('salesReport.orgPicker.onlyThis', 'Only this one')}
                                            </button>
                                        </div>
                                    ))}
                                    {visibleOrgs.length === 0 && (
                                        <p className="px-3 py-2 text-xs text-muted-foreground"
                                           data-testid="report-org-no-match">
                                            {t('salesReport.orgPicker.noMatch', 'No organization matches')}
                                        </p>
                                    )}
                                </div>
                            </div>
                            <p className="mt-2 text-xs text-muted-foreground" data-testid="report-org-summary">
                                {allOrgs
                                    ? t('salesReport.orgPicker.allSelected', 'All organizations selected')
                                    : t('salesReport.orgPicker.selected', '{{count}} organizations selected',
                                        { count: selectedOrgIds.length })}
                            </p>
                        </div>
                    )}

                    {/* Report settings — fixed-width fields that wrap; can never overlap */}
                    <div className="flex flex-wrap gap-x-8 gap-y-4">
                        <div className="w-56">
                            <Label className="mb-2 flex items-center gap-1.5">
                                <FileText className="w-3.5 h-3.5 text-purple-500" />
                                {t('salesReport.reportType')}
                            </Label>
                            <Select value={reportType} onValueChange={setReportType}>
                                <SelectTrigger data-testid="report-type">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="monthly">{t('salesReport.reportTypes.monthly')}</SelectItem>
                                    <SelectItem value="quarterly">{t('salesReport.reportTypes.quarterly')}</SelectItem>
                                    <SelectItem value="annual">{t('salesReport.reportTypes.annual')}</SelectItem>
                                    <SelectItem value="custom">{t('salesReport.reportTypes.custom')}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="w-52">
                            <Label className="mb-2 flex items-center gap-1.5">
                                <CalendarClock className="w-3.5 h-3.5 text-blue-500" />
                                {t('salesReport.startDate')}
                            </Label>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className="w-full px-3 py-2 border border-slate-200 rounded-md"
                                data-testid="start-date"
                            />
                        </div>
                        <div className="w-52">
                            <Label className="mb-2 flex items-center gap-1.5">
                                <CalendarClock className="w-3.5 h-3.5 text-blue-500" />
                                {t('salesReport.endDate')}
                            </Label>
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                className="w-full px-3 py-2 border border-slate-200 rounded-md"
                                data-testid="end-date"
                            />
                        </div>
                        <div className="w-56">
                            <Label className="mb-2 flex items-center gap-1.5">
                                <Globe className="w-3.5 h-3.5 text-orange-500" />
                                {t('salesReport.reportLanguage.label')}
                            </Label>
                            <Select value={reportLanguage} onValueChange={setReportLanguage}>
                                <SelectTrigger data-testid="report-language-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="preferred">{t('salesReport.reportLanguage.preferred')}</SelectItem>
                                    <SelectItem value="en">English</SelectItem>
                                    <SelectItem value="fr">Français</SelectItem>
                                    <SelectItem value="es">Español</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* Include Sections */}
                    <div>
                        <Label className="mb-3 block">{t('salesReport.schedule.sectionsTitle')}</Label>
                        <div className="grid sm:grid-cols-3 gap-3">
                            {['roi', 'platforms', 'employees', 'locations', 'timeline', 'topPerformers'].map(key => (
                                <div key={key} className="flex items-center gap-2">
                                    <Checkbox
                                        id={key}
                                        checked={sections[key]}
                                        onCheckedChange={(checked) => 
                                            setSections({ ...sections, [key]: checked })
                                        }
                                    />
                                    <Label htmlFor={key} className="cursor-pointer">
                                        {t(`salesReport.sections.${key}`)}
                                    </Label>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* PDF Branding — report-level defaults inherited by Preview,
                        Download, one-time sends and scheduled reports */}
                    <PdfBrandingSettings selectedOrg={selectedOrg} />

                    {/* Generate Buttons */}
                    <div className="flex flex-wrap gap-3 pt-4 border-t">
                        <Button 
                            onClick={generateReport} 
                            disabled={loading || noneChosen}
                            data-testid="preview-report"
                        >
                            {loading ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <Eye className="w-4 h-4 mr-2" />
                            )}
                            {loading ? t('salesReport.loadingPreview') : t('salesReport.previewReport')}
                        </Button>
                        <Button
                            onClick={previewPdf}
                            disabled={pdfPreview.loading || noneChosen}
                            variant="outline"
                            className="border-emerald-500/50 text-emerald-500 hover:bg-emerald-500/10 hover:text-emerald-400"
                            data-testid="preview-pdf-btn"
                        >
                            {pdfPreview.loading ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <FileText className="w-4 h-4 mr-2" />
                            )}
                            {pdfPreview.loading ? t('salesReport.generatingPdf') : t('salesReport.previewPdf')}
                        </Button>
                        <Button 
                            onClick={downloadPdf} 
                            disabled={!reportData || generatingPdf || noneChosen}
                            variant="outline"
                            className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10 hover:text-orange-300"
                            data-testid="download-pdf"
                        >
                            {generatingPdf ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <Download className="w-4 h-4 mr-2" />
                            )}
                            {t('salesReport.downloadPdf')}
                        </Button>
                        <Button
                            onClick={() => { setEditingSchedule(null); setShowScheduleModal(true); }}
                            variant="outline"
                            className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10 hover:text-purple-300"
                            data-testid="schedule-report-btn"
                            data-insight-id="schedule-report-btn"
                        >
                            <CalendarClock className="w-4 h-4 mr-2" />
                            {t('salesReport.schedule.btn')}
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Quick Reports */}
            <Card>
                <CardHeader>
                    <CardTitle>{t('salesReport.quickReports')}</CardTitle>
                    <CardDescription>{t('salesReport.quickReportsDesc')}</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                        <Button 
                            variant="outline" 
                            onClick={() => quickReport('this_month')}
                            disabled={loading}
                            className="justify-start"
                            data-testid="quick-this-month"
                        >
                            <FileText className="w-4 h-4 mr-2" />
                            {t('salesReport.quick.thisMonth')}
                        </Button>
                        <Button 
                            variant="outline" 
                            onClick={() => quickReport('last_month')}
                            disabled={loading}
                            className="justify-start"
                            data-testid="quick-last-month"
                        >
                            <FileText className="w-4 h-4 mr-2" />
                            {t('salesReport.quick.lastMonth')}
                        </Button>
                        <Button 
                            variant="outline" 
                            onClick={() => quickReport('qtd')}
                            disabled={loading}
                            className="justify-start"
                            data-testid="quick-qtd"
                        >
                            <FileText className="w-4 h-4 mr-2" />
                            {t('salesReport.quick.qtd')}
                        </Button>
                        <Button 
                            variant="outline" 
                            onClick={() => quickReport('ytd')}
                            disabled={loading}
                            className="justify-start"
                            data-testid="quick-ytd"
                        >
                            <FileText className="w-4 h-4 mr-2" />
                            {t('salesReport.quick.ytd')}
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Scheduled Reports */}
            <ScheduledReportsList
                organizations={organizations}
                refreshNonce={scheduleRefresh}
                onEdit={(s) => { setEditingSchedule(s); setShowScheduleModal(true); }}
            />

            <ScheduleReportModal
                open={showScheduleModal}
                onOpenChange={setShowScheduleModal}
                organizations={organizations}
                isSuper={isSuperAdmin()}
                inherited={{ selectedOrg, selectedOrgIds: orgIdsForRequest, sections, reportType, startDate, endDate }}
                editing={editingSchedule}
                onSaved={() => setScheduleRefresh(n => n + 1)}
            />

            <PdfPreviewModal
                open={pdfPreview.open}
                onOpenChange={(o) => setPdfPreview(p => ({ ...p, open: o }))}
                loading={pdfPreview.loading}
                error={pdfPreview.error}
                pages={pdfPreview.pages}
                onRetry={previewPdf}
                title={t('salesReport.pdfPreview.title')}
            />

            {/* Report Preview */}
            {showPreview && reportData && (
                <div ref={reportRef} className="space-y-6">
                    {/* Executive Summary */}
                    {sections.roi && (
                        <Card>
                            <CardHeader className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-t-lg">
                                <CardTitle className="text-white">{t('salesReport.executiveSummary')}</CardTitle>
                                <CardDescription className="text-emerald-100">
                                    {reportData.report_info.organization} | {reportData.report_info.start_date} – {reportData.report_info.end_date}
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="pt-6">
                                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                                    <div className="text-center p-4 bg-emerald-50 rounded-lg">
                                        <div className="text-3xl font-bold text-emerald-600" data-testid="summary-value">
                                            {formatCurrency(reportData.executive_summary.total_value)}
                                        </div>
                                        <div className="text-sm text-muted-foreground">{t('pdfExports.sales.cardTotalValue')}</div>
                                    </div>
                                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                                        <div className="text-3xl font-bold text-blue-600" data-testid="summary-taps">
                                            {reportData.executive_summary.total_taps.toLocaleString()}
                                        </div>
                                        <div className="text-sm text-muted-foreground">{t('pdfExports.sales.cardTotalTaps')}</div>
                                    </div>
                                    <div className="text-center p-4 bg-purple-50 rounded-lg">
                                        <div className="text-3xl font-bold text-purple-600">
                                            {reportData.executive_summary.avg_taps_per_day}
                                        </div>
                                        <div className="text-sm text-muted-foreground">{t('pdfExports.sales.cardAvgTapsDay')}</div>
                                    </div>
                                    <div className={`text-center p-4 rounded-lg ${
                                        reportData.executive_summary.growth_vs_previous >= 0 
                                            ? 'bg-green-50' 
                                            : 'bg-red-50'
                                    }`}>
                                        <div className={`text-3xl font-bold flex items-center justify-center gap-1 ${
                                            reportData.executive_summary.growth_vs_previous >= 0 
                                                ? 'text-green-600' 
                                                : 'text-red-600'
                                        }`}>
                                            {reportData.executive_summary.growth_vs_previous >= 0 ? (
                                                <TrendingUp className="w-6 h-6" />
                                            ) : (
                                                <TrendingDown className="w-6 h-6" />
                                            )}
                                            {reportData.executive_summary.growth_vs_previous}%
                                        </div>
                                        <div className="text-sm text-muted-foreground">vs Previous Period</div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Business Outcomes — Review Shield & Voucher Gift Cards */}
                    {(reportData.review_shield || reportData.voucher_gift_cards) && (
                        <div className="grid sm:grid-cols-2 gap-6">
                            {reportData.review_shield && (
                                <Card data-testid="report-review-shield">
                                    <CardHeader className="pb-2">
                                        <CardTitle className="flex items-center gap-2 text-base">
                                            <ShieldCheck className="w-5 h-5 text-emerald-600" />
                                            {t('salesReport.outcomes.reviewShield')}
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        {!reportData.review_shield.active ? (
                                            <Badge variant="secondary" data-testid="review-shield-off">{t('salesReport.outcomes.off')}</Badge>
                                        ) : (
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <div className="text-2xl font-bold text-emerald-600" data-testid="review-shield-generated">
                                                        {reportData.review_shield.reviews_generated}
                                                    </div>
                                                    <div className="text-xs text-muted-foreground">{t('salesReport.outcomes.reviewsGenerated')}</div>
                                                </div>
                                                <div>
                                                    <div className="text-2xl font-bold text-red-500" data-testid="review-shield-shielded">
                                                        {reportData.review_shield.reviews_shielded}
                                                    </div>
                                                    <div className="text-xs text-muted-foreground">{t('salesReport.outcomes.reviewsShielded')}</div>
                                                </div>
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            )}
                            {reportData.voucher_gift_cards && (
                                <Card data-testid="report-vouchers">
                                    <CardHeader className="pb-2">
                                        <CardTitle className="flex items-center gap-2 text-base">
                                            <Gift className="w-5 h-5 text-purple-600" />
                                            {t('salesReport.outcomes.vouchers')}
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        {!reportData.voucher_gift_cards.active ? (
                                            <Badge variant="secondary" data-testid="vouchers-off">{t('salesReport.outcomes.off')}</Badge>
                                        ) : (
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <div className="text-2xl font-bold text-foreground" data-testid="vouchers-distributed">
                                                        {reportData.voucher_gift_cards.distributed}
                                                    </div>
                                                    <div className="text-xs text-muted-foreground">{t('salesReport.outcomes.distributed')}</div>
                                                </div>
                                                <div>
                                                    <div className="text-2xl font-bold text-emerald-600" data-testid="vouchers-redeemed">
                                                        {reportData.voucher_gift_cards.redeemed}
                                                    </div>
                                                    <div className="text-xs text-muted-foreground">{t('salesReport.outcomes.redeemed')}</div>
                                                </div>
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            )}
                        </div>
                    )}

                    {/* Platform Breakdown */}
                    {sections.platforms && (
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <PieChart className="w-5 h-5 text-purple-600" />
                                    {t('pdfExports.sales.sectionPlatformBreakdown')}
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="grid lg:grid-cols-2 gap-6">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>{t('pdfExports.sales.colPlatform')}</TableHead>
                                                <TableHead className="text-right">{t('pdfExports.sales.colTaps')}</TableHead>
                                                <TableHead className="text-right">{t('pdfExports.sales.colValuePerTap')}</TableHead>
                                                <TableHead className="text-right">{t('pdfExports.sales.colTotalValue')}</TableHead>
                                                <TableHead className="text-right">%</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {Object.entries(reportData.platform_breakdown).map(([platform, data]) => (
                                                <TableRow key={platform}>
                                                    <TableCell className="font-medium">
                                                        <div className="flex items-center gap-2">
                                                            <div 
                                                                className="w-3 h-3 rounded-full"
                                                                style={{ backgroundColor: PLATFORM_COLORS[platform] }}
                                                            />
                                                            {PLATFORM_LABELS[platform] || platform}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className="text-right">{data.count}</TableCell>
                                                    <TableCell className="text-right">{formatCurrency(data.value_per_tap)}</TableCell>
                                                    <TableCell className="text-right font-semibold">{formatCurrency(data.total_value)}</TableCell>
                                                    <TableCell className="text-right">{data.percentage}%</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>

                                    {platformChartData.length > 0 && (
                                        <div className="h-[300px]">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <RechartsPie>
                                                    <Pie
                                                        data={platformChartData}
                                                        cx="50%"
                                                        cy="50%"
                                                        innerRadius={60}
                                                        outerRadius={100}
                                                        paddingAngle={2}
                                                        dataKey="value"
                                                    >
                                                        {platformChartData.map((entry, index) => (
                                                            <Cell key={`cell-${index}`} fill={entry.fill} />
                                                        ))}
                                                    </Pie>
                                                    <RechartsTooltip 
                                                        formatter={(value) => formatCurrency(value)}
                                                    />
                                                </RechartsPie>
                                            </ResponsiveContainer>
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Employee Performance */}
                    {sections.employees && reportData.employee_performance.length > 0 && (
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Users className="w-5 h-5 text-blue-600" />
                                    {t('salesReport.sections.employees')}
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>{t('pdfExports.sales.colRank')}</TableHead>
                                            <TableHead>{t('pdfExports.sales.colEmployee')}</TableHead>
                                            <TableHead className="text-right">{t('pdfExports.sales.colTotalTaps')}</TableHead>
                                            <TableHead>{t('salesReport.platformDistribution')}</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {reportData.employee_performance.slice(0, 10).map((emp, index) => (
                                            <TableRow key={emp.employee_id || index}>
                                                <TableCell>
                                                    {index < 3 ? (
                                                        <Badge className={
                                                            index === 0 ? 'bg-yellow-500' :
                                                            index === 1 ? 'bg-slate-400' :
                                                            'bg-amber-600'
                                                        }>
                                                            #{index + 1}
                                                        </Badge>
                                                    ) : (
                                                        <span className="text-muted-foreground">#{index + 1}</span>
                                                    )}
                                                </TableCell>
                                                <TableCell className="font-medium">{emp.employee_name}</TableCell>
                                                <TableCell className="text-right font-semibold">{emp.total_taps}</TableCell>
                                                <TableCell>
                                                    <div className="flex gap-1 flex-wrap">
                                                        {Object.entries(emp.platform_breakdown || {}).slice(0, 3).map(([p, count]) => (
                                                            <Badge key={p} variant="outline" className="text-xs">
                                                                {PLATFORM_LABELS[p]?.split(' ')[0] || p}: {count}
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    )}

                    {/* Location Performance */}
                    {sections.locations && reportData.location_performance.length > 0 && (
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <MapPin className="w-5 h-5 text-red-600" />
                                    {t('pdfExports.sales.sectionLocation')}
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>{t('pdfExports.sales.colLocation')}</TableHead>
                                            <TableHead className="text-right">{t('pdfExports.sales.colTotalTaps')}</TableHead>
                                            <TableHead className="text-right">{t('salesReport.valueGenerated')}</TableHead>
                                            <TableHead>{t('pdfExports.sales.colTopPlatform')}</TableHead>
                                            <TableHead>{t('pdfExports.sales.colTopEmployee')}</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {reportData.location_performance.map((loc, index) => (
                                            <TableRow key={loc.location_id}>
                                                <TableCell className="font-medium">{loc.location_name}</TableCell>
                                                <TableCell className="text-right">{loc.total_taps}</TableCell>
                                                <TableCell className="text-right font-semibold">{formatCurrency(loc.total_value)}</TableCell>
                                                <TableCell>
                                                    <Badge variant="outline">
                                                        {PLATFORM_LABELS[loc.top_platform] || loc.top_platform}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell>{loc.top_employee}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    )}

                    {/* Daily Activity Timeline */}
                    {sections.timeline && reportData.daily_activity.length > 0 && (
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <BarChart3 className="w-5 h-5 text-indigo-600" />
                                    {t('salesReport.sections.timeline')}
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="h-[300px]">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart data={reportData.daily_activity}>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                            <XAxis 
                                                dataKey="date" 
                                                tick={{ fontSize: 10 }}
                                                tickLine={false}
                                            />
                                            <YAxis 
                                                tick={{ fontSize: 12 }}
                                                tickLine={false}
                                                axisLine={false}
                                            />
                                            <RechartsTooltip />
                                            <Line 
                                                type="monotone" 
                                                dataKey="taps" 
                                                stroke="#6366F1" 
                                                strokeWidth={2}
                                                dot={false}
                                                name={t('pdfExports.sales.colTaps')}
                                            />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Recommendations */}
                    {(reportData.recommendations_i18n?.length > 0 || reportData.recommendations.length > 0) && (
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Lightbulb className="w-5 h-5 text-yellow-500" />
                                    {t('pdfExports.sales.sectionRecommendations')}
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3">
                                    {(reportData.recommendations_i18n?.length > 0
                                        ? reportData.recommendations_i18n.map((r) => ({
                                            text: t(`pdfExports.sales.${r.key}`, { ...r.params, defaultValue: '' }) || '',
                                            positive: r.key === 'recGrowthUp',
                                        })).filter((r) => r.text)
                                        : reportData.recommendations.map((rec) => ({
                                            text: rec,
                                            positive: rec.includes('Great') || rec.includes('up'),
                                        }))
                                    ).map((rec, index) => (
                                        <div 
                                            key={index}
                                            className="flex items-start gap-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg"
                                        >
                                            {rec.positive ? (
                                                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                                            ) : (
                                                <AlertCircle className="w-5 h-5 text-yellow-500 mt-0.5" />
                                            )}
                                            <span className="text-yellow-900">{rec.text}</span>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </div>
            )}
        </div>
    );
};

export default SalesReport;

